<script module lang="ts">
	// DOM id of the injected Apollo tracker script. Exported so other surfaces
	// (e.g. the cookie banner) can detect whether the pixel is currently loaded
	// without duplicating the magic string.
	export const APOLLO_SCRIPT_ID = 'apollo-website-tracker';
</script>

<script lang="ts">
	import { env } from '$env/dynamic/public';
	import { cookieConsent } from '$lib/utils/cookieConsent.svelte';
	import { onMount } from 'svelte';

	let loaded = $state(false);

	function trackerId(): string | undefined {
		return env.PUBLIC_APOLLO_TRACKER_ID;
	}

	function shouldLoad(): boolean {
		if (!trackerId()) return false;
		if (loaded) return false;
		// Opt-in regions (EU/UK/EEA/CH): require explicit accept.
		// Elsewhere: load unless the visitor has rejected.
		if (cookieConsent.requiresOptIn) {
			return cookieConsent.state === 'accepted';
		}
		return cookieConsent.state !== 'rejected';
	}

	function injectScript() {
		if (loaded || typeof document === 'undefined') return;
		if (document.getElementById(APOLLO_SCRIPT_ID)) {
			loaded = true;
			return;
		}
		const id = trackerId();
		if (!id) return;
		const script = document.createElement('script');
		script.id = APOLLO_SCRIPT_ID;
		script.async = true;
		script.defer = true;
		script.src = `https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache=${Math.random().toString(36).slice(2)}&v=4096`;
		// Apollo's IIFE exposes window.trackingFunctions.onLoad — calling it
		// with the appId is what actually starts the tracker (fires track_request,
		// identifies the visitor, etc.). Without this the script just sits idle.
		script.onload = () => {
			const fns = (window as unknown as { trackingFunctions?: { onLoad?: (a: { appId: string }) => void } })
				.trackingFunctions;
			fns?.onLoad?.({ appId: id });
		};
		document.head.appendChild(script);
		loaded = true;
	}

	onMount(() => {
		cookieConsent.hydrate();
		// Resolve geo before deciding — for non-EU visitors we want the pixel
		// to fire on the first page view, not after the banner choice.
		cookieConsent.resolveCountry().then(() => {
			if (shouldLoad()) injectScript();
		});
	});

	$effect(() => {
		// Re-evaluate whenever consent changes (user clicked Accept / Reject /
		// reopened preferences).
		void cookieConsent.state;
		if (shouldLoad()) injectScript();
	});
</script>
